// Abo-Konfigurator-Drawer — startet aus dem Korb.
// Übernimmt Cart-Items als Abo-Items, fragt Wochentage/Intervall/Start.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/stores/cartStore";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";
import { type Customer, useCustomerAddresses } from "@/lib/db";
import { useCreateSubscription, WEEKDAY_LABEL } from "@/lib/subscriptions";
import { type PaymentMethod } from "@/lib/orders";
import { formatDateIso } from "@/lib/calendar";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export default function AboKonfiguratorDrawer({ onClose }: { onClose: () => void }) {
  const user = useAuth((s) => s.user);
  const items = useCart((s) => s.items);
  const totalCents = useCart((s) => s.totalCents());
  const clear = useCart((s) => s.clear);
  const create = useCreateSubscription();
  const nav = useNavigate();

  const [customer, setCustomer] = useState<Customer | null>(null);
  useEffect(() => {
    if (!user) return;
    supabase.from("customers").select("*").eq("profile_id", user.id).maybeSingle()
      .then(({ data }) => setCustomer(data as Customer | null));
  }, [user]);

  const { data: addresses = [] } = useCustomerAddresses(customer?.id);
  const [addressId, setAddressId] = useState("");
  useEffect(() => {
    if (!addressId && addresses.length) {
      setAddressId(addresses.find((a) => a.is_default)?.id ?? addresses[0].id);
    }
  }, [addresses, addressId]);

  const [weekdays, setWeekdays] = useState<number[]>([3]); // Default Mi
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [startsOn, setStartsOn] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return formatDateIso(d);
  });
  const [hasEnd, setHasEnd] = useState(false);
  const [endsOn, setEndsOn] = useState("");
  const [payment, setPayment] = useState<PaymentMethod>("cash");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  function toggleWeekday(wd: number) {
    setWeekdays((cur) => cur.includes(wd) ? cur.filter((x) => x !== wd) : [...cur, wd].sort());
  }

  async function onSubmit() {
    setErr(null);
    if (!customer || !addressId) return setErr("Lieferadresse fehlt.");
    if (!items.length) return setErr("Korb ist leer.");
    if (!weekdays.length) return setErr("Mindestens einen Wochentag wählen.");
    try {
      await create.mutateAsync({
        customer_id: customer.id,
        address_id: addressId,
        weekdays,
        interval_weeks: intervalWeeks,
        starts_on: startsOn,
        ends_on: hasEnd ? endsOn : null,
        payment_method: payment,
        notes: notes.trim() || null,
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
      });
      clear();
      onClose();
      nav("/shop/abos", { replace: true });
    } catch (ex) {
      setErr((ex as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-hof-900/40 flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-hof-50 w-full max-w-lg h-full overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-hof-900">Als Abo bestellen</h2>
          <button onClick={onClose} className="btn-ghost p-2">✕</button>
        </div>

        <section className="card">
          <h3 className="font-semibold text-hof-800 mb-2">Diese Artikel werden regelmäßig geliefert</h3>
          <div className="divide-y divide-hof-100">
            {items.map((i) => (
              <div key={i.product_id} className="py-2 flex justify-between text-sm">
                <span>{i.qty}× {i.name}</span>
                <span className="text-hof-700">{fmt.format((i.qty * i.unit_price_cents) / 100)}</span>
              </div>
            ))}
            <div className="pt-2 flex justify-between font-bold">
              <span>Je Lieferung</span>
              <span className="text-wiese-700">{fmt.format(totalCents / 100)}</span>
            </div>
          </div>
        </section>

        <section className="card space-y-3">
          <h3 className="font-semibold text-hof-800">An welchen Tagen?</h3>
          <div className="flex gap-1 flex-wrap">
            {Object.entries(WEEKDAY_LABEL).map(([wd, label]) => {
              const day = parseInt(wd);
              const on = weekdays.includes(day);
              return (
                <button key={wd} type="button" onClick={() => toggleWeekday(day)}
                  className={`min-w-touch min-h-touch px-4 rounded-xl border-2 font-medium ${
                    on ? "border-wiese-500 bg-wiese-50 text-wiese-800" : "border-hof-200 text-hof-600 hover:bg-hof-100"
                  }`}>
                  {label}
                </button>
              );
            })}
          </div>
          <div>
            <label className="label">Intervall</label>
            <select className="input" value={intervalWeeks} onChange={(e) => setIntervalWeeks(parseInt(e.target.value))}>
              <option value={1}>Jede Woche</option>
              <option value={2}>Alle 2 Wochen</option>
              <option value={3}>Alle 3 Wochen</option>
              <option value={4}>Alle 4 Wochen</option>
            </select>
          </div>
        </section>

        <section className="card space-y-3">
          <div>
            <label className="label">Start ab</label>
            <input type="date" className="input" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={hasEnd} onChange={(e) => setHasEnd(e.target.checked)} className="w-5 h-5" />
            <span>Mit End-Datum</span>
          </label>
          {hasEnd && (
            <input type="date" className="input" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
          )}
        </section>

        <section className="card">
          <label className="label">Zahlart</label>
          <select className="input" value={payment} onChange={(e) => setPayment(e.target.value as PaymentMethod)}>
            <option value="cash">Bar bei Lieferung</option>
            <option value="invoice">Auf Rechnung</option>
          </select>
        </section>

        <section className="card">
          <label className="label">Notiz</label>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </section>

        {err && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">{err}</div>}

        <button onClick={onSubmit} disabled={create.isPending} className="btn-primary w-full py-4 text-lg">
          {create.isPending ? "Anlegen…" : "Abo starten"}
        </button>
        <p className="text-xs text-hof-500 text-center">
          Pausieren oder kündigen können Sie jederzeit unter "Mein Abo".
        </p>
      </div>
    </div>
  );
}
